// Session and tenant context.
//
// The app is multi-tenant: every request is scoped to an organization, a
// school and an academic session. Those three ids are read from localStorage
// on nearly every screen, so they live here rather than being re-read ad hoc.

import { setTenantContext } from '@/lib/analytics';
import { apiRequest } from './api';

const KEYS = {
  token: 'auth_token',
  permissions: 'permissions',
  organizationId: 'organization_id',
  schoolId: 'school_id',
  academicSessionId: 'academic_session_id',
  schoolName: 'school_name',
  userName: 'user_name',
} as const;

export interface LoginData {
  token: string;
  permissions: string[];
  name?: string;
  role?: string;
  /**
   * The user's own id, when the server sends one.
   *
   * Read defensively rather than assumed: nothing in v1 ever used it, so
   * whether `/api/login` returns it is unverified. Analytics identifies the
   * user by this when it is present and stays anonymous when it is not — see
   * `lib/analytics.ts`. v1 identified by *email*, which splits a person's
   * history the day their address changes.
   */
  id?: string;
}

/* ── Token ──────────────────────────────────────────────────────────────── */

export const getToken = () => localStorage.getItem(KEYS.token);
export const setToken = (token: string) => localStorage.setItem(KEYS.token, token);

export function clearSession() {
  for (const key of Object.values(KEYS)) localStorage.removeItem(key);
}

/* ── Tenant context ─────────────────────────────────────────────────────── */

export const getOrganizationId = () => localStorage.getItem(KEYS.organizationId);
export const getSchoolId = () => localStorage.getItem(KEYS.schoolId);
export const getAcademicSessionId = () => localStorage.getItem(KEYS.academicSessionId);
export const getUserName = () => localStorage.getItem(KEYS.userName) ?? '';

export const getSchoolName = () => localStorage.getItem(KEYS.schoolName);
export const setAcademicSessionId = (id: string) => localStorage.setItem(KEYS.academicSessionId, id);

/**
 * Pushes the three stored ids into analytics as super properties.
 *
 * Lives here because this is where the ids live, and because three callers need
 * it and none of them can own it: the app shell at startup, the sign-in
 * callback, and the title bar's switchers. Missing any one of them files events
 * under the wrong school — which is the dimension the whole scheme exists to
 * segment by. Cheap enough to call on every change; it only writes a local
 * object that later events are stamped from.
 */
export function syncAnalyticsContext(): void {
  setTenantContext({
    organizationId: getOrganizationId(),
    schoolId: getSchoolId(),
    academicSessionId: getAcademicSessionId(),
  });
}

/**
 * Select a school, or clear the selection for the "All schools" view.
 *
 * The name is cached alongside the id so the title bar can label the current
 * school before the schools list has loaded — v1 did the same, and the receipt
 * printer reads it too.
 */
export function selectSchool(school: { id: string; name: string } | null) {
  if (school) {
    localStorage.setItem(KEYS.schoolId, school.id);
    localStorage.setItem(KEYS.schoolName, school.name);
  } else {
    localStorage.removeItem(KEYS.schoolId);
    localStorage.removeItem(KEYS.schoolName);
  }
}

/** Initials for the avatar. "Ankit Tiwari" → "AT", "Ankit" → "AN". */
export function getUserInitials(): string {
  const name = getUserName().trim();
  if (!name) return '?';
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ── Permissions ────────────────────────────────────────────────────────── */

export function getPermissions(): string[] {
  try {
    const raw = localStorage.getItem(KEYS.permissions);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function setPermissions(permissions: string[]) {
  localStorage.setItem(KEYS.permissions, JSON.stringify(permissions));
}

/* ── Login ──────────────────────────────────────────────────────────────── */

export async function login(email: string, password: string): Promise<LoginData> {
  const res = await apiRequest<LoginData>('/api/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return res.data;
}

/**
 * Loads the org/school/session the signed-in user belongs to and caches the
 * ids. Called once after login and once on app start for an existing session.
 */
export async function fetchAndStoreUserContext(): Promise<void> {
  const res = await apiRequest<{
    organization?: { id: string };
    school?: { id: string };
    academic_session?: { id: string };
    name?: string;
  }>('/api/me/context');

  const { organization, school, academic_session, name } = res.data;
  if (organization) localStorage.setItem(KEYS.organizationId, organization.id);
  if (school) localStorage.setItem(KEYS.schoolId, school.id);
  if (academic_session) localStorage.setItem(KEYS.academicSessionId, academic_session.id);
  if (name) localStorage.setItem(KEYS.userName, name);
}
